/** Обсуждение заявки: вопросы согласующих и закупщика, ответы автора.
 *
 * Автор: «ГД спрашивает — зачем и куда покупается? Это должно фиксироваться
 * в заявке, и потом закупщик это видит». Вопрос не возвращает заявку на
 * доработку и не двигает маршрут — это разговор рядом с решением.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { errorDetail } from "../lib/errors";
import { ROLE_LABEL, type Role } from "../lib/labels";
import Icon from "./Icon";
import { Badge, Button, Textarea } from "../ui";

export interface DiscussionMessage {
  comment_id: string;
  author_id: string | null;
  author_name: string | null;
  author_position: string | null;
  author_role: string | null;
  is_initiator: boolean;
  body: string;
  created_at: string | null;
}

export const discussionKey = (requestId: string) => ["requests", requestId, "discussion"] as const;

export interface UnreadDiscussion {
  process_id: string;
  number: string | null;
  title: string | null;
  count: number;
  last_by_name: string | null;
  last_body: string;
  last_at: string;
  awaiting_me: boolean;
}

export const UNREAD_KEY = ["discussion-unread"] as const;

/** Новые сообщения в обсуждениях — колокольчик, метки в списках, счётчик в меню. */
export function useDiscussionUnread() {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => api.get<UnreadDiscussion[]>("/requests/discussion/unread"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Новые сообщения по заявкам — для меток в строках списков. */
export function useUnreadByRequest(): Map<string, UnreadDiscussion> {
  const unread = useDiscussionUnread();
  return useMemo(() => new Map((unread.data ?? []).map((u) => [u.process_id, u])), [unread.data]);
}

/** Метка в строке списка: «💬 новое сообщение» / «ждут вашего ответа». */
export function UnreadMark({ requestId }: { requestId: string }) {
  const item = useUnreadByRequest().get(requestId);
  if (!item) return null;
  return (
    <Link
      to={`/requests/${requestId}#discussion`}
      className="mt-0.5 flex w-fit items-center gap-1 text-xs font-medium text-accent hover:underline"
      data-testid="unread-mark"
    >
      <Icon name="message" size={12} />
      {item.awaiting_me ? "ждут вашего ответа" : item.count > 1 ? `новых сообщений: ${item.count}` : "новое сообщение"}
    </Link>
  );
}

export function useRequestDiscussion(requestId: string | undefined) {
  return useQuery({
    queryKey: discussionKey(requestId ?? ""),
    queryFn: () => api.get<DiscussionMessage[]>(`/requests/${requestId}/discussion`),
    enabled: Boolean(requestId),
  });
}

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

function who(m: DiscussionMessage): string {
  if (m.is_initiator) return "автор заявки";
  const parts = [m.author_position, m.author_role ? ROLE_LABEL[m.author_role as Role] : null].filter(Boolean);
  return parts.join(", ").toLowerCase();
}

export default function RequestDiscussion({
  requestId,
  initiatorId,
  title = "Обсуждение заявки",
  hideWhenEmpty = false,
}: {
  requestId: string;
  initiatorId?: string | null;
  title?: string;
  /** В закупке: пустое обсуждение свёрнуто в кнопку «Спросить автора». */
  hideWhenEmpty?: boolean;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const thread = useRequestDiscussion(requestId);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const isAuthor = Boolean(user && initiatorId && String(user.id) === String(initiatorId));
  const post = useMutation({
    mutationFn: (body: string) => api.post<DiscussionMessage[]>(`/requests/${requestId}/discussion`, { body }),
    onSuccess: (data) => {
      qc.setQueryData(discussionKey(requestId), data);
      setText("");
      // Статус «ждёт ответа автора» живёт в заявке — перечитать её и очереди.
      void qc.invalidateQueries({ queryKey: ["requests"] });
      void qc.invalidateQueries({ queryKey: ["processes"] });
    },
  });

  // Открыл обсуждение — сервер отметил прочитанным: обновить колокольчик.
  useEffect(() => {
    if (thread.dataUpdatedAt) void qc.invalidateQueries({ queryKey: UNREAD_KEY });
  }, [thread.dataUpdatedAt, qc]);
  // Пришли из колокольчика (#discussion) — сразу к обсуждению.
  useEffect(() => {
    if (thread.isSuccess && window.location.hash === "#discussion") {
      document.getElementById("discussion")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [thread.isSuccess]);

  const messages = thread.data ?? [];
  const last = messages[messages.length - 1];
  const awaitingAuthor = Boolean(last && !last.is_initiator);

  if (hideWhenEmpty && messages.length === 0 && !open) {
    if (thread.isPending) return null;
    return (
      <div>
        <Button variant="secondary" size="sm" icon="message" onClick={() => setOpen(true)}>
          Спросить автора заявки
        </Button>
      </div>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (body) post.mutate(body);
  };

  return (
    <section
      id="discussion"
      className="card scroll-mt-20 space-y-3"
      aria-labelledby={`discussion-${requestId}`}
      data-testid="request-discussion"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id={`discussion-${requestId}`} className="font-medium">
          {title}
        </h2>
        {awaitingAuthor && (
          <Badge tone="warning">{isAuthor ? "Ждут вашего ответа" : "Ждёт ответа автора"}</Badge>
        )}
        {last?.is_initiator && <Badge tone="success">Автор ответил</Badge>}
      </div>
      {thread.isError && (
        <p role="alert" className="text-sm text-danger-ink">
          Обсуждение не загрузилось: {errorDetail(thread.error)}
        </p>
      )}
      {messages.length === 0 && !thread.isPending && (
        <p className="text-sm text-muted">
          {isAuthor
            ? "Вопросов пока нет. Если согласующий спросит — вопрос появится здесь, и придёт письмо."
            : "Нужно уточнить — зачем, куда, для кого? Спросите автора здесь: вопрос и ответ останутся в заявке, их увидит и закупщик."}
        </p>
      )}
      {messages.length > 0 && (
        <ol className="space-y-2">
          {messages.map((m) => (
            <li
              key={m.comment_id}
              className={`rounded-lg border px-3 py-2 text-sm ${
                m.is_initiator ? "border-accent-line bg-accent-soft sm:ml-8" : "border-line bg-sunken sm:mr-8"
              }`}
            >
              <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted">
                <span className="font-medium text-ink">{m.author_name || "—"}</span>
                {who(m) && <span>{who(m)}</span>}
                <span className="ml-auto text-faint">{when(m.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-ink">{m.body}</p>
            </li>
          ))}
        </ol>
      )}
      <form onSubmit={submit} className="space-y-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          maxLength={4000}
          aria-label={isAuthor ? "Ответ в обсуждение заявки" : "Вопрос автору заявки"}
          placeholder={
            isAuthor
              ? awaitingAuthor
                ? "Ответьте: зачем, куда, для кого…"
                : "Пояснение к заявке"
              : "Вопрос автору: зачем, куда, для кого…"
          }
        />
        {post.isError && (
          <p role="alert" className="text-sm text-danger-ink">
            {errorDetail(post.error)}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-xs text-faint">
            {isAuthor ? "Спросившим придёт письмо." : "Автору придёт письмо. Решение по заявке — отдельно."}
          </span>
          <Button type="submit" size="sm" variant="primary" loading={post.isPending} disabled={!text.trim()}>
            {isAuthor ? "Ответить" : "Спросить"}
          </Button>
        </div>
      </form>
    </section>
  );
}
